import { IApiError, BackendSource } from '../apiTypes';

/**
 * Error parser specifically for .NET backend API errors
 */
export class DotNetErrorParser {
  /**
   * Extracts correlation ID from multiple sources
   */
  private static getCorrelationId(error: any, responseData?: any): string | undefined {
    // First try to get from response body (both camelCase and PascalCase)
    if (responseData?.correlationId) {
      return responseData.correlationId;
    }
    if (responseData?.CorrelationId) {
      return responseData.CorrelationId;
    }
    // Then try response headers
    if (error.response?.headers?.['x-correlation-id']) {
      return error.response.headers['x-correlation-id'];
    }
    // Finally try request headers
    if (error.config?.headers?.['x-correlation-id']) {
      return error.config.headers['x-correlation-id'];
    }
    return undefined;
  }

  /**
   * Parses a .NET backend error response
   */
  static parseError(error: any, baseUrl: string = ''): IApiError {
    const requestUrl = error.config?.url || '';

    // If error response contains standardized API error format
    if (error.response?.data) {
      const data = error.response.data;

      // Normalize property names (handle both camelCase and PascalCase)
      const normalizedData = {
        message: data.message || data.Message || data.errorMessage || data.ErrorMessage || data.title || data.Title || '',
        statusCode: data.statusCode || data.StatusCode || data.status || data.Status || error.response.status || 500,
        stack: data.stack || data.Stack || data.stackTrace || data.StackTrace || '',
        url: data.url || data.Url || requestUrl,
        timestamp: data.timestamp || data.Timestamp || new Date().toISOString(),
        correlationId: data.correlationId || data.CorrelationId
      };

      if (normalizedData.message && typeof normalizedData.statusCode === 'number') {
        return {
          message: normalizedData.message,
          stack: normalizedData.stack,
          url: normalizedData.url || requestUrl,
          statusCode: normalizedData.statusCode,
          timestamp: normalizedData.timestamp,
          correlationId: normalizedData.correlationId || this.getCorrelationId(error, data),
          backendSource: 'dotnet' as BackendSource
        };
      }
    }

    // Handle network errors
    if (error.request && !error.response) {
      return {
        message: error.message || 'Kunde inte ansluta till servern. Kontrollera din internetanslutning.',
        stack: error.stack || '',
        url: requestUrl,
        statusCode: 0,
        timestamp: new Date().toISOString(),
        correlationId: this.getCorrelationId(error),
        backendSource: 'dotnet' as BackendSource
      };
    }

    // Fallback for other errors
    return {
      message: error.message || 'Ett oväntat fel uppstod',
      stack: error.stack || '',
      url: requestUrl,
      statusCode: error.response?.status || 500,
      timestamp: new Date().toISOString(),
      correlationId: this.getCorrelationId(error),
      backendSource: 'dotnet' as BackendSource
    };
  }
}
